import React, {useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Button, CircularProgress} from '@mui/material';
import {GridActionsCellItem} from '@mui/x-data-grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CancelIcon from '@mui/icons-material/Close';
import {useConfirm} from 'material-ui-confirm';
import {ROLES} from '../../../../../constants/roles';
import {useParams} from 'react-router-dom';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import {grey} from '@mui/material/colors';
import AdmicityTable from '../../../../../shared-components/AdmicityTable';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import {validateEmail, validateFieldLength} from '../../../../../utility/validationUtil';
import AdmicityForm from '../../../../../shared-components/AdmicityForm';
import {
    useAddContactMutation,
    useDeleteContactMutation,
    useGetContactsQuery,
    useManageConfirmMutation,
    useUpdateContactMutation
} from '../../../../../api/services/profilesService';
import useUser from '../../../../../utility/hooks/useUser';
import ContactEmergencyRoundedIcon from '@mui/icons-material/ContactEmergencyRounded';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import {formatDate} from '../../../../../utility/dateUtil';
import Typography from '@mui/material/Typography';
import {retrieveProfile} from '../../ProfilesSlice';

const Contacts = ({readonly}) => {
    const {
        selectedProfile,
        contactRelations,
        currentSchool,
    } = useSelector(state => state.profilesInfo);
    const {studentId} = useParams();
    const [openCreateContactDialog, setOpenCreateContactDialog] = useState(false);
    const [openEditContactDialog, setOpenEditContactDialog] = useState(false);
    const [selectedContact, setSelectedContact] = useState({});
    const [isChildFormValid, setIsChildFormValid] = useState(false);
    const formRef = useRef(null);
    const {user} = useUser();
    const confirm = useConfirm();
    const dispatch = useDispatch();

    const [addContact, {isLoading: isAddContactInProgress}] = useAddContactMutation();
    const [updateContact, {isLoading: isUpdateContactInProgress}] = useUpdateContactMutation();
    const [deleteContact] = useDeleteContactMutation();
    const [manageConfirm] = useManageConfirmMutation();

    const {
        data: contacts = {options: []},
        isLoading: isContactsLoading,
    } = useGetContactsQuery({studentId, role: user.role});

    const columns = [
        {field: 'forename', headerName: 'Forename', minWidth: 120, flex: 1, sortable: false},
        {field: 'surname', headerName: 'Surname', minWidth: 120, flex: 1, sortable: false},
        {
            field: 'email',
            headerName: 'Email',
            minWidth: 170,
            flex: 1,
            sortable: false,
            renderCell: hiddenCellFormatter('email')
        },
        {
            field: 'relationType',
            headerName: 'Relation Type',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                if (user.role === ROLES.PARENT && params.row.userId !== user.id) {
                    return <span style={{color: grey[500]}}>hidden</span>;
                }
            }
        },
        {
            field: 'priority',
            headerName: 'Priority',
            minWidth: 70,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (user.role === ROLES.PARENT && params.row.userId !== user.id) {
                    return <span style={{color: grey[500]}}>hidden</span>;
                }
            }
        },
        {
            field: 'legalOrder',
            headerName: 'Legal Order',
            minWidth: 80,
            flex: 1,
            type: 'boolean',
            sortable: false,
            renderCell: hiddenCellFormatter('legalOrder')
        },
        {
            field: 'parentalResponsibility',
            headerName: 'Parental Responsibility',
            minWidth: 80,
            flex: 1,
            type: 'boolean',
            sortable: false,
            renderCell: hiddenCellFormatter('parentalResponsibility')
        },
        ...(
            readonly
                ? []
                : [
                    {
                        field: 'actions',
                        type: 'actions',
                        headerName: 'Actions',
                        minWidth: 70,
                        flex: 1,
                        cellClassName: 'actions',
                        getActions: ({row}) => {
                            if (user.role === ROLES.MEMBER_OF_STAFF || user.role === ROLES.SCHOOL_ADMIN) {
                                return [
                                    <GridActionsCellItem
                                        key={'editIcon'}
                                        icon={<EditIcon/>}
                                        label="Edit"
                                        className="textPrimary"
                                        onClick={() => handleEditClick(row)}
                                        color="inherit"
                                    />,
                                    <GridActionsCellItem
                                        key={'deleteIcon'}
                                        icon={<DeleteIcon/>}
                                        label="Delete"
                                        onClick={() => handleDeleteClick(row)}
                                        color="inherit"
                                    />,
                                ];
                            } else if (user.role === ROLES.PARENT && row.userId === user.id) {
                                return [
                                    <GridActionsCellItem
                                        key={'editIcon'}
                                        icon={<EditIcon/>}
                                        label="Edit"
                                        className="textPrimary"
                                        onClick={() => handleEditClick(row)}
                                        color="inherit"
                                    />,
                                ];
                            } else {
                                return [];
                            }
                        },
                    }
                ]
        )
    ];

    function hiddenCellFormatter(columnKey) {
        const CustomFormatter = ({row, value}) => {
            if (user.role === ROLES.PARENT && row.userId !== user.id) {
                return <span style={{color: grey[500]}}>hidden</span>;
            }

            if (typeof value === 'boolean') {
                return value
                    ? (<CheckIcon/>)
                    : (<CancelIcon style={{color: grey[500]}}/>);
            }
            return <span>{row[columnKey]}</span>;
        };

        CustomFormatter.propTypes = {
            row: PropTypes.object.isRequired,
            value: PropTypes.object.isRequired,
        };

        return CustomFormatter;
    }

    const handleEditClick = (contact) => {
        setSelectedContact(contact);
        setOpenEditContactDialog(true);
    };

    const handleDeleteClick = async (contact) => {
        await deleteContact({studentId, contactId: contact.id});
    };

    const handleDialogClose = () => {
        setOpenCreateContactDialog(false);
        setOpenEditContactDialog(false);
        setSelectedContact({});
    };

    const handleSaveContact = async (payload) => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the contact section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(async () => {
                if (openCreateContactDialog) {
                    await addContact({studentId, body: {studentId, ...payload}})
                        .then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
                } else {
                    await updateContact({
                        studentId,
                        role: user.role,
                        body: {studentId, contact: {...selectedContact, ...payload}}
                    }).then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
                }

                setSelectedContact({});
                handleDialogClose();
            })
            .catch(() => {
            });
    };

    const handleConfirm = () => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the contact section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(async () => {
                await manageConfirm({studentId: selectedProfile.id, type: STUDENT_PROFILE_CONFIRMATION_TYPES.Contacts})
                    .then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));
            })
            .catch(() => {
            });
    };

    const validateUniqueEmail = (value, existingContacts, currentEmail) => {
        const emailValidationResults = validateEmail(value);

        if (!emailValidationResults.isValid || value === currentEmail) {
            return emailValidationResults;
        }

        /* eslint-disable */
        const existingContactWithEmail = existingContacts.find(contact =>
            contact.email?.trim().toUpperCase() === value.trim().toUpperCase()
        );
        /* eslint-disable */

        const isValid = !existingContactWithEmail;
        const message = isValid ? '' : 'Contact with the same email already exist';

        return {isValid, message};
    };

    const textFields = [
        {
            label: 'Forename',
            name: 'forename',
            validate: (value) => validateFieldLength(value, 128)
        },
        {
            label: 'Surname',
            name: 'surname',
            validate: (value) => validateFieldLength(value, 128)
        },
        {
            label: 'Email',
            name: 'email',
            type: 'email',
            validate: (value) => validateUniqueEmail(value, contacts.contacts, selectedContact.email)
        },
        {
            label: 'Relation Type',
            name: 'relationType',
            select: true,
            options: contactRelations.map(element => ({
                value: element.description,
                title: element.description
            }))
        },
        {
            label: 'Priority',
            name: 'priority',
            validate: (value) => {
                let errorMessage = '';

                if (!value || value.length === 0) {
                    errorMessage = 'This field is required.';
                } else if (!/^-?[0-9]+$/.test(value)) {
                    errorMessage = 'Only numbers are allowed.';
                } else if (parseInt(value) <= 0) {
                    errorMessage = 'The Priority cannot be less than 1';
                } else if (parseInt(value) > 10) {
                    errorMessage = 'The Priority cannot be more than 10';
                }

                return {
                    isValid: errorMessage === '',
                    message: errorMessage,
                };
            }
        },
    ];

    const options = [
        {
            label: 'Legal Order',
            name: 'legalOrder',
        },
        {
            label: 'Parental Responsibility',
            name: 'parentalResponsibility',
        }
    ];

    return (
        isContactsLoading
            ? ''
            : <>
                <Box
                    marginTop={1}
                    marginBottom={1}
                >
                    {
                        readonly
                            ? ''
                            : <>
                                {user.role !== ROLES.PARENT
                                    ? <Button
                                        color="primary"
                                        variant="contained"
                                        startIcon={<AddCircleOutlineIcon/>}
                                        onClick={() => setOpenCreateContactDialog(true)}>
                                        Add contact
                                    </Button>
                                    : <></>
                                }
                                {!contacts?.contactsConfirmedAt
                                    ? <Button sx={{mb: 2, my: 2, ml: 1}} variant="contained"
                                              onClick={handleConfirm}>Confirm</Button>
                                    : <Typography sx={{mb: 2, mt: 2, ml: 1, display: 'inline-block'}}>Confirmed
                                        at {formatDate(contacts?.contactsConfirmedAt)} by {contacts?.contactsConfirmedBy}</Typography>
                                }
                            </>
                    }
                </Box>
                <AdmicityTable
                    sx={{
                        height: 'auto',
                        maxHeight: 600
                    }}
                    data={contacts.contacts ?? []}
                    columns={columns}
                    pageSize={contacts.contacts?.length ?? 0}
                    noRowsOverlay={{
                        icon: <ContactEmergencyRoundedIcon/>,
                        text: 'No contacts has been added'
                    }}
                    hideFooterPagination
                />
                <AdmicityDialog
                    handleClose={handleDialogClose}
                    title={openCreateContactDialog ? 'Add new contact' : 'Edit contact'}
                    open={openCreateContactDialog || openEditContactDialog}
                    actions={[
                        {label: 'Cancel', onClick: handleDialogClose},
                        {
                            label: 'Save',
                            disabled: isAddContactInProgress || isUpdateContactInProgress || !isChildFormValid,
                            startIcon: (isAddContactInProgress || isUpdateContactInProgress)
                                ? <CircularProgress color="inherit" size={16}/>
                                : undefined,
                            onClick: () => {
                                if (typeof formRef.current.requestSubmit === 'function') {
                                    formRef.current.requestSubmit();
                                } else {
                                    formRef.current.dispatchEvent(new Event('submit', {cancelable: true}));
                                }
                            }
                        }
                    ]}
                >
                    <AdmicityForm
                        ref={formRef}
                        textFields={openCreateContactDialog
                            ? textFields
                            : textFields.map(field => ({...field, initialValue: selectedContact[field.name] ?? ''}))}
                        options={openCreateContactDialog
                            ? options
                            : options.map(field => ({...field, initialValue: selectedContact[field.name] ?? ''}))}
                        handleSubmit={handleSaveContact}
                        buttonText="Save"
                        setIsChildFormValid={setIsChildFormValid}
                        actionsContainerProps={{
                            container: {
                                display: 'none'
                            }
                        }}
                    />
                </AdmicityDialog>
            </>
    );
};

Contacts.propTypes = {
    readonly: PropTypes.bool
};

export default Contacts;
